import { useState } from "react";
import { useNavigate } from "react-router";
import type { Programa } from "@/interfaces/programa.interface";
import type { ActividadPrograma } from "@/interfaces/actividadPrograma.interface";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Layers } from "lucide-react";
import { RegisterActividadProgramaForm } from "@/components/forms/actividades-programas/RegisterActividadProgramaForm";
import { EditActividadProgramaForm } from "@/components/forms/actividades-programas/EditActividadProgramaForm";
import { SectionHeader } from "@/components/layout/SectionHeader";
import { ActivityCard } from "@/features/programas/components/ActivityCard";

const getAprendicesAsignados = (
  actividadId: number,
  aprendices: any[],
): number => {
  return aprendices.filter((aprendiz) =>
    aprendiz.actividades.some((act: any) => act.idActividad === actividadId),
  ).length;
};

const getPorcentajeCompletitud = (
  actividadId: number,
  aprendices: any[],
): number => {
  const asignados = getAprendicesAsignados(actividadId, aprendices);
  if (asignados === 0) return 0;
  return Math.min((asignados / aprendices.length) * 100, 100);
};

interface ProgramaActividadesProps {
  programa: Programa;
  onRefetch: () => void;
  aprendices: any[];
  isSuperAdmin: boolean;
  onActividadReporte: (actividad: ActividadPrograma) => void;
}

export function ProgramaActividades({
  programa,
  onRefetch,
  aprendices,
  isSuperAdmin,
  onActividadReporte,
}: ProgramaActividadesProps) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEditOpen, setModalEditOpen] = useState(false);
  const [actividadToEdit, setActividadToEdit] =
    useState<ActividadPrograma | null>(null);

  return (
    <>
      <section className="space-y-6">
        <SectionHeader
          icon={Layers}
          title="Actividades del programa"
          description="Gestiona las actividades formativas que componen el programa. Define fechas, límites de intentos y materiales de referencia para cada actividad."
          action={
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
              <DialogTrigger asChild>
                <Button>Agregar actividad</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Registrar actividad</DialogTitle>
                </DialogHeader>
                <RegisterActividadProgramaForm
                  idPrograma={programa.id}
                  setOpen={setModalOpen}
                  refetch={onRefetch}
                />
              </DialogContent>
            </Dialog>
          }
        />
        {Array.isArray(programa?.actividadPrograma) &&
        programa.actividadPrograma.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {programa.actividadPrograma.map((actividad: ActividadPrograma) => (
              <ActivityCard
                key={actividad.id}
                actividad={actividad}
                aprendicesAsignados={getAprendicesAsignados(
                  actividad.id,
                  aprendices,
                )}
                porcentajeCompletitud={getPorcentajeCompletitud(
                  actividad.id,
                  aprendices,
                )}
                onEdit={() => {
                  setActividadToEdit(actividad);
                  setModalEditOpen(true);
                }}
                onReporte={() => {
                  onActividadReporte(actividad);
                }}
                isSuperAdmin={isSuperAdmin}
                onPreview={() =>
                  navigate(
                    `/admin/programas/detalles/${programa.id}/preview-actividad/${actividad.id}`,
                  )
                }
              />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-black/10 bg-[var(--background-muted-secondary)]/60 px-6 py-8 text-center">
            <Layers className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-sm text-muted-foreground mb-2">
              No hay actividades registradas
            </p>
            <p className="text-xs text-muted-foreground">
              Agrega tu primera actividad para comenzar el programa
            </p>
          </div>
        )}
      </section>

      {/* Modal de edición de actividad */}
      <Dialog open={modalEditOpen} onOpenChange={setModalEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar actividad</DialogTitle>
          </DialogHeader>
          {actividadToEdit && (
            <EditActividadProgramaForm
              data={{
                id: actividadToEdit.id,
                idPrograma: actividadToEdit.idPrograma,
                nombre: actividadToEdit.nombre,
                rol: actividadToEdit.rol ?? "",
                archivoLimpio:
                  actividadToEdit.archivoLimpio?.llave &&
                  actividadToEdit.archivoLimpio?.nombre
                    ? {
                        llave: actividadToEdit.archivoLimpio.llave,
                        nombre: actividadToEdit.archivoLimpio.nombre,
                        url: actividadToEdit.archivoLimpio.url,
                      }
                    : null,
                archivoWord:
                  actividadToEdit.archivoWord?.llave &&
                  actividadToEdit.archivoWord?.nombre
                    ? {
                        llave: actividadToEdit.archivoWord.llave,
                        nombre: actividadToEdit.archivoWord.nombre,
                        url: actividadToEdit.archivoWord.url,
                      }
                    : null,
                fechaInicio: actividadToEdit.fechaInicio
                  ? new Date(actividadToEdit.fechaInicio)
                  : null,
                fechaFin: actividadToEdit.fechaFin
                  ? new Date(actividadToEdit.fechaFin)
                  : null,
              }}
              close={() => {
                setModalEditOpen(false);
                setActividadToEdit(null);
                onRefetch();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
